"use client";

import React, { useState } from 'react';
import Link from 'next/link';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar, TrendingUp, Wrench, Clock } from 'lucide-react';
import { whatsNewData, CompletedItem } from './changelog-data';
import { format } from 'date-fns';

interface ChangelogModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ChangelogModal({ open, onOpenChange }: ChangelogModalProps) {
  const formatDate = (dateStr: string) => {
    try {
      return format(new Date(dateStr), 'MMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  const renderSimpleList = (items: string[], emptyMessage: string) => {
    if (!items || items.length === 0) {
      return (
        <div className="text-center py-8 text-muted-foreground">
          {emptyMessage}
        </div>
      );
    }

    return (
      <ul className="space-y-3">
        {items.map((item, index) => (
          <li key={index} className="text-sm text-muted-foreground flex items-start gap-3">
            <span className="text-muted-foreground mt-1">•</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    );
  };

  const renderCompletedList = (items: CompletedItem[]) => {
    if (!items || items.length === 0) {
      return (
        <div className="text-center py-8 text-muted-foreground">
          No recent shipments to show.
        </div>
      );
    }

    // Group items by date
    const groupedByDate = items.reduce((acc, item) => {
      const date = item.date;
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(item);
      return acc;
    }, {} as Record<string, CompletedItem[]>);

    // Sort dates in descending order (most recent first)
    const sortedDates = Object.keys(groupedByDate).sort((a, b) => 
      new Date(b).getTime() - new Date(a).getTime()
    );

    return (
      <div className="space-y-6">
        {sortedDates.map((date) => (
          <div key={date} className="space-y-3">
            {/* Date header */}
            <div className="flex items-center gap-3">
              <h4 className="font-medium text-sm">{formatDate(date)}</h4>
              <div className="flex-1 h-px bg-border"></div>
              <Badge variant="outline" className="text-xs">
                {groupedByDate[date].length} update{groupedByDate[date].length !== 1 ? 's' : ''}
              </Badge>
            </div>
            
            {/* Items for this date */}
            <ul className="space-y-2 ml-4">
              {groupedByDate[date].map((item, index) => (
                <li key={index} className="border-l-2 border-green-500/20 pl-4 py-1">
                  <span className="text-sm text-muted-foreground">{item.text}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            TabLab Roadmap
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            TabLab ships fast and continuously. See what we've shipped, what we're building, and what's coming next.
          </p>
        </DialogHeader>
        
        <Tabs defaultValue="shipped" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="shipped" className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Recently Shipped
              <Badge variant="outline" className="text-xs">
                {whatsNewData.recentlyCompleted.length}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="working" className="flex items-center gap-2">
              <Wrench className="h-4 w-4" />
              Working On
              <Badge variant="outline" className="text-xs">
                {whatsNewData.currentlyWorking.length}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="coming" className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Coming Next
              <Badge variant="outline" className="text-xs">
                {whatsNewData.comingNext.length}
              </Badge>
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="shipped" className="mt-4">
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-green-500" />
                <h3 className="font-semibold text-lg">Recently Shipped</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Fresh features and improvements that just went live. We ship early to get you the best features fast. Sometimes we might introduce bugs. If we do, then please{' '}
                <Link href="https://tablab.featurebase.app" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">submit feedback</Link>.
              </p>
              <ScrollArea className="h-[50vh] pr-4">
                {renderCompletedList(whatsNewData.recentlyCompleted)}
              </ScrollArea>
            </div>
          </TabsContent>
          
          <TabsContent value="working" className="mt-4">
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Wrench className="h-5 w-5 text-orange-500" />
                <h3 className="font-semibold text-lg">Currently Working On</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                What we're actively building right now. These features are in development.
              </p>
              <ScrollArea className="h-[50vh] pr-4">
                {renderSimpleList(whatsNewData.currentlyWorking, "No active development items.")}
              </ScrollArea>
            </div>
          </TabsContent>
          
          <TabsContent value="coming" className="mt-4">
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-blue-500" />
                <h3 className="font-semibold text-lg">Coming Next</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                What we're planning to work on next. Get a preview of TabLab's future direction.
              </p>
              <ScrollArea className="h-[50vh] pr-4">
                {renderSimpleList(whatsNewData.comingNext, "No upcoming features planned.")}
              </ScrollArea>
            </div>
          </TabsContent>
        </Tabs>
        
        <div className="text-center pt-4 border-t">
          <p className="text-xs text-muted-foreground">
            🚀 TabLab ships fast with continuous updates. Sometimes we might introduce bugs. If we do, then please{' '}
            <Link href="https://tablab.featurebase.app" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">submit feedback</Link>.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
} 